import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { RideCard } from '../components/RideCard';
import { RideDetailsModal } from '../components/RideDetailsModal';
import { Map, User, Star, Phone, Mail, Calendar, Shield, MessageSquare, X, Check, Clock, MapPin, Users, FileText, CheckCircle, AlertTriangle, Play, Repeat, Trash2, Pause, ArrowRight, ArrowLeft } from 'lucide-react';
import { CameraCheckModal } from '../components/CameraCheckModal';
import { CommunityImpactCard } from '../components/CommunityImpactCard';

import { API as API_URL } from '../utils/apiConfig';

// ── Pendler-Angebote Helper ───────────────────────────────────────
const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const CommuteOfferCard = ({ offer, onPause, onResume, onDelete, busy }) => {
  const days = (offer.weekdays || []).map(d => WEEKDAY_LABELS[d]).join(' · ');
  const paused = !offer.is_active;
  return (
    <div data-testid={`commute-offer-${offer.id}`} style={{
      ...co.card, ...(paused ? co.cardPaused : {}),
    }}>
      <div style={co.headRow}>
        <div style={co.iconBox}>
          <Repeat size={18} color={paused ? '#94a3b8' : '#a855f7'} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={co.title}>
            {offer.label || `${offer.from_city} ↔ ${offer.to_city}`}
          </div>
          <div style={co.daysLine}>{days || '—'}</div>
        </div>
        {paused && (
          <span style={co.pausedBadge} data-testid={`commute-paused-badge-${offer.id}`}>
            <Pause size={11} /> {offer.pause_reason || 'Pausiert'}
          </span>
        )}
      </div>

      <div style={co.routeBlock}>
        {offer.morning_time && (
          <div style={co.routeRow} data-testid={`commute-morning-${offer.id}`}>
            <Clock size={13} color="#fde68a" />
            <span style={co.timeChip}>{offer.morning_time}</span>
            <span style={co.cityCity}>{offer.from_city}</span>
            <ArrowRight size={12} color="#64748b" />
            <span style={co.cityCity}>{offer.to_city}</span>
          </div>
        )}
        {offer.return_time && (
          <div style={co.routeRow} data-testid={`commute-return-${offer.id}`}>
            <Clock size={13} color="#fda4af" />
            <span style={co.timeChip}>{offer.return_time}</span>
            <span style={co.cityCity}>{offer.to_city}</span>
            <ArrowLeft size={12} color="#64748b" />
            <span style={co.cityCity}>{offer.from_city}</span>
          </div>
        )}
      </div>

      <div style={co.metaRow}>
        <span style={co.metaPill}><Users size={11} /> {offer.seats_total} Plätze</span>
        <span style={co.metaPill}>≤ {offer.max_detour_km} km Korridor</span>
      </div>

      <div style={co.actionRow}>
        {paused ? (
          <button onClick={() => onResume(offer.id)} disabled={busy}
                  data-testid={`commute-resume-${offer.id}`}
                  style={{ ...co.btn, ...co.btnResume }}>
            <Play size={14} /> Fortsetzen
          </button>
        ) : (
          <button onClick={() => onPause(offer.id)} disabled={busy}
                  data-testid={`commute-pause-${offer.id}`}
                  style={{ ...co.btn, ...co.btnPause }}>
            <Pause size={14} /> Pausieren
          </button>
        )}
        <button onClick={() => onDelete(offer.id)} disabled={busy}
                data-testid={`commute-delete-${offer.id}`}
                style={{ ...co.btn, ...co.btnDelete }}>
          <Trash2 size={14} /> Löschen
        </button>
      </div>
    </div>
  );
};

const co = {
  card: {
    background: 'linear-gradient(135deg, rgba(15,23,42,0.92), rgba(30,27,75,0.88))',
    backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
    borderWidth: 1, borderStyle: 'solid', borderColor: 'rgba(168,85,247,0.25)',
    borderRadius: 16, padding: 18, marginBottom: 14,
    boxShadow: '0 12px 32px rgba(15,23,42,0.18)',
    color: '#f1f5f9',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  cardPaused: {
    opacity: 0.7,
    background: 'linear-gradient(135deg, rgba(51,65,85,0.85), rgba(30,41,59,0.85))',
    borderColor: 'rgba(148,163,184,0.3)',
  },
  headRow: { display: 'flex', alignItems: 'center', gap: 12 },
  iconBox: {
    width: 38, height: 38, borderRadius: 12, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)',
  },
  title: { fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 2 },
  daysLine: { fontSize: 12, color: '#94a3b8', letterSpacing: 0.5 },
  pausedBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
    background: 'rgba(148,163,184,0.18)', color: '#cbd5e1',
    border: '1px solid rgba(148,163,184,0.4)',
  },
  routeBlock: { display: 'flex', flexDirection: 'column', gap: 6 },
  routeRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 },
  timeChip: {
    fontFamily: 'ui-monospace, monospace', fontSize: 13, fontWeight: 700,
    background: 'rgba(255,255,255,0.05)', padding: '3px 8px', borderRadius: 6,
    color: '#f1f5f9',
  },
  cityCity: { color: '#cbd5e1', fontWeight: 600 },
  metaRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  metaPill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 11, color: '#94a3b8',
    background: 'rgba(255,255,255,0.04)', padding: '4px 10px', borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.06)',
  },
  actionRow: { display: 'flex', gap: 8 },
  btn: {
    flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  },
  btnPause: { background: 'rgba(251,191,36,0.18)', color: '#fde68a' },
  btnResume: { background: 'rgba(34,197,94,0.2)', color: '#bbf7d0' },
  btnDelete: { background: 'rgba(239,68,68,0.15)', color: '#fca5a5' },
};

const MyRides = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-US' : 'de-DE';
  const { user } = useAuth();
  const [myBookings, setMyBookings] = useState([]);
  const [myRidesAsDriver, setMyRidesAsDriver] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined') {
      const tab = new URLSearchParams(window.location.search).get('tab');
      if (tab === 'commutes' || tab === 'driver') return tab;
    }
    return 'bookings';
  });
  
  // Pendler-Angebote (Creator-Mode)
  const [commuteOffers, setCommuteOffers] = useState([]);
  const [commuteOffersLoading, setCommuteOffersLoading] = useState(false);
  const [busyOfferId, setBusyOfferId] = useState(null);
  
  const [selectedRideForModal, setSelectedRideForModal] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Dispute state
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeBooking, setDisputeBooking] = useState(null);
  
  // Camera check state
  const [showCameraCheck, setShowCameraCheck] = useState(false);
  const [cameraCheckRideId, setCameraCheckRideId] = useState(null);
  
  // Neue State für Passagierprofil
  const [showPassengerModal, setShowPassengerModal] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [passengerLoading, setPassengerLoading] = useState(false);
  const [passengerRatings, setPassengerRatings] = useState([]);

  useEffect(() => {
    fetchMyBookings();
    fetchMyRidesAsDriver();
    fetchCommuteOffers();
  }, []);

  const fetchCommuteOffers = async () => {
    try {
      setCommuteOffersLoading(true);
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/commute-offers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommuteOffers(r.data.offers || []);
    } catch (err) {
      console.error('Error fetching commute offers:', err);
    } finally {
      setCommuteOffersLoading(false);
    }
  };

  const handlePauseOffer = async (offerId) => {
    const reason = window.prompt(t('myRides.commute.pausePrompt',
      'Grund (optional): z. B. „Urlaub", „Krank", „Homeoffice"'), '');
    if (reason === null) return;  // User pressed Cancel
    setBusyOfferId(offerId);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/commute-offers/${offerId}/pause`,
        { reason: reason || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setCommuteOffers(prev => prev.map(o =>
        o.id === offerId ? { ...o, is_active: false, pause_reason: reason || null } : o));
    } catch (err) {
      console.error('Pause failed:', err);
      alert(t('myRides.commute.pauseFailed', 'Pause fehlgeschlagen.'));
    } finally {
      setBusyOfferId(null);
    }
  };

  const handleResumeOffer = async (offerId) => {
    setBusyOfferId(offerId);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/commute-offers/${offerId}/resume`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setCommuteOffers(prev => prev.map(o =>
        o.id === offerId ? { ...o, is_active: true, pause_reason: null } : o));
    } catch (err) {
      console.error('Resume failed:', err);
      alert(t('myRides.commute.resumeFailed', 'Fortsetzen fehlgeschlagen.'));
    } finally {
      setBusyOfferId(null);
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm(t('myRides.commute.confirmDelete',
      'Pendler-Angebot wirklich komplett löschen?'))) return;
    setBusyOfferId(offerId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/commute-offers/${offerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommuteOffers(prev => prev.filter(o => o.id !== offerId));
    } catch (err) {
      console.error('Delete failed:', err);
      alert(t('myRides.commute.deleteFailed', 'Löschen fehlgeschlagen.'));
    } finally {
      setBusyOfferId(null);
    }
  };

  const fetchMyBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/bookings/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyBookings(response.data.bookings || []);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRidesAsDriver = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/rides/my-rides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const ridesWithBookings = await Promise.all(
        response.data.rides.map(async (ride) => {
          try {
            const bookingsRes = await axios.get(
              `${API_URL}/rides/${ride.ride_id}/bookings`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            return { ...ride, bookings: bookingsRes.data.bookings || [] };
          } catch (err) {
            return { ...ride, bookings: [] };
          }
        })
      );
      
      setMyRidesAsDriver(ridesWithBookings);
    } catch (err) {
      console.error('Error fetching rides as driver:', err);
    }
  };

  // Verbesserte Funktion zum Laden des Passagierprofils
  const handleViewPassengerProfile = async (passengerId, passengerName) => {
    try {
      setPassengerLoading(true);
      const token = localStorage.getItem('token');
      
      console.log(`📱 Loading profile for passenger: ${passengerId} (${passengerName})`);
      
      // Versuche den neuen Endpoint zuerst
      try {
        const response = await axios.get(`${API_URL}/users/${passengerId}/profile`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            accept: 'application/json'
          }
        });
        
        console.log('✅ Profile loaded successfully:', response.data.user.name);
        setSelectedPassenger(response.data.user);
        setPassengerRatings(response.data.recent_ratings || []);
        setShowPassengerModal(true);
        
      } catch (error) {
        console.log('Profile endpoint failed, trying public profile...', error);
        
        // Fallback 1: Versuche public profile
        try {
          const response = await axios.get(`${API_URL}/public-profile/${passengerId}`);
          
          console.log('✅ Public profile loaded:', response.data.name);
          setSelectedPassenger(response.data);
          setPassengerRatings([]);
          setShowPassengerModal(true);
          
        } catch (error2) {
          console.log('Public profile failed, using basic info...', error2);
          
          // Fallback 2: Zeige nur Basisdaten aus Buchung
          const booking = myRidesAsDriver
            .flatMap(ride => ride.bookings || [])
            .find(b => b.passenger_id === passengerId);
          
          setSelectedPassenger({
            id: passengerId,
            name: passengerName || 'Gast-Mitfahrer',
            average_rating: booking?.passenger?.rating || 4.5,
            total_ratings: booking?.passenger?.rating_count || 10,
            verification_status: 'basic',
            total_rides_as_passenger: booking?.passenger?.total_rides || 5,
            bio: 'Diese Person nutzt unsere Mitfahrgelegenheit-Plattform.',
            location: booking?.passenger?.city || 'Deutschland',
            phone: booking?.passenger?.phone || '',
            email: booking?.passenger?.email || ''
          });
          setPassengerRatings([]);
          setShowPassengerModal(true);
        }
      }
      
    } catch (err) {
      console.error('Fehler beim Laden des Passagierprofils:', err);
      
      // Minimaler Fallback
      setSelectedPassenger({
        name: passengerName || 'Gast-Mitfahrer',
        average_rating: 4.8,
        total_ratings: 15,
        verification_status: 'basic',
        total_rides_as_passenger: 8,
        bio: 'Regelmäßiger Nutzer unserer Plattform.',
        location: 'Deutschland'
      });
      setPassengerRatings([]);
      setShowPassengerModal(true);
      
    } finally {
      setPassengerLoading(false);
    }
  };

  const handleConfirm = async (bookingId, passengerName) => {
    if (!window.confirm(`Buchung von ${passengerName} wirklich annehmen?`)) return;
    
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/bookings/${bookingId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchMyRidesAsDriver();
      alert('Buchung bestätigt!');
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (bookingId, passengerName) => {
    if (!window.confirm(`Buchung von ${passengerName} wirklich ablehnen?`)) return;
    
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/bookings/${bookingId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchMyRidesAsDriver();
      alert('Buchung abgelehnt!');
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (bookingId) => {
    if (!window.confirm('Fahrt abschließen und Zahlung freigeben?')) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/bookings/${bookingId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchMyRidesAsDriver();
      alert('Fahrt erfolgreich abgeschlossen!');
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      alert('Bitte gib einen Grund an.');
      return;
    }
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/bookings/${selectedBooking.booking_id}/cancel`, 
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchMyBookings();
      await fetchMyRidesAsDriver();
      setShowCancelModal(false);
      setCancelReason('');
      alert('Erfolgreich storniert.');
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitRating = async () => {
    if (!comment.trim()) {
      alert('Bitte gib einen Kommentar ab.');
      return;
    }
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const isPassenger = selectedBooking.passenger_id === user.id;
      const ratedUserId = isPassenger ? selectedBooking.driver.id : selectedBooking.passenger_id;
      
      await axios.post(`${API_URL}/ratings/submit`, {
        booking_id: selectedBooking.booking_id,
        rated_user_id: ratedUserId,
        rating: rating,
        comment: comment
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      await fetchMyBookings();
      await fetchMyRidesAsDriver();
      setShowRatingModal(false);
      setComment('');
      alert('Bewertung gesendet!');
    } catch (err) {
      alert('Fehler beim Bewerten: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitDispute = async () => {
    if (!disputeReason.trim()) {
      alert(t('disputes.disputeReason'));
      return;
    }
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/disputes/booking/${disputeBooking.booking_id}`,
        { reason: disputeReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchMyBookings();
      setShowDisputeModal(false);
      setDisputeReason('');
      setDisputeBooking(null);
      alert(t('disputes.disputeCreated'));
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      alert(detail);
    } finally {
      setActionLoading(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colors[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const calculateRefundInfo = (ride, booking) => {
    const departureDateTime = new Date(`${ride.departure_date}T${ride.departure_time}`);
    const now = new Date();
    const hoursUntilDeparture = (departureDateTime - now) / (1000 * 60 * 60);
    const basePrice = booking.price_breakdown.base_price;
    
    if (hoursUntilDeparture > 24) {
      return { amount: basePrice, percentage: 100, message: 'Volle Rückerstattung' };
    } else if (hoursUntilDeparture > 0) {
      return { amount: basePrice * 0.5, percentage: 50, message: '50% Rückerstattung (< 24h)' };
    } else {
      return { amount: 0, percentage: 0, message: 'Keine Rückerstattung (Bereits abgefahren)' };
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-lg border border-gray-100 px-6 py-5 mb-6">
        <h1 className="text-3xl font-bold mb-5 text-gray-900">{t('myRides.title')}</h1>

        <div className="flex space-x-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`pb-3 px-4 font-semibold transition ${activeTab === 'bookings' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-700 hover:text-gray-900'}`}
          >
            {t('myRides.tabs.asPassenger')}
          </button>
          <button
            onClick={() => setActiveTab('driver')}
            className={`pb-3 px-4 font-semibold transition ${activeTab === 'driver' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-700 hover:text-gray-900'}`}
          >
            {t('myRides.tabs.asDriver')}
          </button>
          <button
            onClick={() => setActiveTab('commutes')}
            data-testid="tab-commutes"
            className={`pb-3 px-4 font-semibold transition ${activeTab === 'commutes' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-700 hover:text-gray-900'}`}
          >
            {t('myRides.tabs.commutes', 'Pendler-Angebote')}
            {commuteOffers.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-purple-100 text-purple-700">
                {commuteOffers.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">{error}</div>}

      {activeTab === 'driver' && (
        <div className="mb-6">
          <CommunityImpactCard token={localStorage.getItem('token')} />
        </div>
      )}

      {activeTab === 'bookings' && (
        <div className="space-y-6">
          {myBookings.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm border">
              <p className="text-gray-500">{t('myRides.empty.passenger')}</p>
              <button 
                onClick={() => window.location.href='/search'} 
                className="mt-4 text-blue-600 font-semibold hover:underline"
              >
                {t('myRides.findRide')}
              </button>
            </div>
          ) : (
            myBookings.map((booking) => (
              <div key={booking.booking_id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                <RideCard 
                  ride={booking.ride} 
                  onClick={() => {
                    setSelectedBooking(booking);
                    setSelectedRideForModal(booking.ride);
                  }} 
                />

                <div className="p-4 bg-white border-t">
                  <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-sm">
                      {(booking.ride?.driver_name || "D").charAt(0)}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{t('common.driver')}</p>
                      <p className="font-bold text-gray-800">{booking.ride?.driver_name || t('common.driver')}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                    <div>
                      <p className="text-gray-500">{t('admin.users.status')}</p>
                      <span className={`font-bold ${booking.status === 'cancelled' ? 'text-red-600' : 'text-green-600'}`}>
                        {t(`myRides.status.${booking.status}`)}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-500">{t('common.seats')}</p>
                      <p className="font-bold">{booking.seats_booked}x {t('common.booked')}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">{t('createRide.pricing.serviceFee')}</p>
                      <p className="font-bold">{Number(booking.price_breakdown?.service_fee ?? 0).toFixed(2)}€</p>
                    </div>
                    <div>
                      <p className="text-gray-500">{t('common.price')}</p>
                      <p className="font-bold text-blue-700 text-base">{Number(booking.price_breakdown?.total ?? 0).toFixed(2)}€</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    {(booking.status === 'pending' || booking.status === 'confirmed') && (
                      <button 
                        onClick={() => { 
                          setSelectedBooking(booking); 
                          setShowCancelModal(true); 
                        }} 
                        className="flex-1 bg-white border-2 border-red-600 text-red-600 py-2.5 rounded-lg font-bold hover:bg-red-50 transition"
                      >
                        {t('myRides.actions.cancel')}
                      </button>
                    )}
                    
                    {booking.status === 'completed' && !booking.rated_by_passenger && (
                      <button 
                        onClick={() => { 
                          setSelectedBooking(booking); 
                          setShowRatingModal(true); 
                        }} 
                        className="flex-1 bg-yellow-500 text-white py-2.5 rounded-lg font-bold hover:bg-yellow-600 shadow-sm transition"
                      >
                        ⭐ {t('myRides.actions.rate')}
                      </button>
                    )}

                    {booking.status === 'completed' && booking.rated_by_passenger && (
                      <div className="flex-1 text-center text-green-600 font-bold py-2 bg-green-50 rounded-lg border border-green-200">
                        ✓ {t('ratings.thanks')}
                      </div>
                    )}

                    {booking.payment_status === 'paid' && (
                      <button
                        data-testid={`download-receipt-${booking.booking_id}`}
                        onClick={() => {
                          const token = localStorage.getItem('token');
                          window.open(`${API_URL}/receipts/${booking.booking_id}/pdf?token=${token}`, '_blank');
                        }}
                        className="flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-50 transition text-sm"
                      >
                        <FileText size={16} /> {t('myRides.actions.downloadReceipt')}
                      </button>
                    )}

                    {booking.payment_status === 'paid' && booking.status !== 'cancelled' && (
                      <button
                        data-testid={`dispute-btn-${booking.booking_id}`}
                        onClick={() => {
                          setDisputeBooking(booking);
                          setShowDisputeModal(true);
                        }}
                        className="flex items-center gap-1.5 bg-white border border-orange-400 text-orange-600 py-2 px-4 rounded-lg font-semibold hover:bg-orange-50 transition text-sm"
                      >
                        <AlertTriangle size={16} /> {t('disputes.openDispute')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'driver' && (
        <div className="space-y-6">
          {myRidesAsDriver.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm border">
              <p className="text-gray-500 mb-4">{t('myRides.empty.driver')}</p>
              <button 
                onClick={() => window.location.href='/create-ride'} 
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 transition"
              >
                {t('myRides.createFirst')}
              </button>
            </div>
          ) : (
            myRidesAsDriver.map((ride) => (
              <div key={ride.ride_id} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                <div className="cursor-pointer" onClick={() => setSelectedRideForModal(ride)}>
                   <RideCard ride={ride} onClick={() => setSelectedRideForModal(ride)} />
                </div>

                <div className="p-5 border-t bg-gray-50/50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-gray-700 flex items-center">
                      <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs mr-2">
                        {ride.bookings?.length || 0}
                      </span>
                      {t('myRides.bookings.title')}
                    </h4>
                    {ride.status === 'available' && ride.bookings?.some(b => b.payment_status === 'paid') && (
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`start-ride-${ride.ride_id}`}
                          onClick={() => {
                            setCameraCheckRideId(ride.ride_id);
                            setShowCameraCheck(true);
                          }}
                          className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-blue-700 transition disabled:opacity-50"
                        >
                          <Play size={16} /> {t('myRides.actions.startRide')}
                        </button>
                        <button
                          data-testid={`complete-ride-${ride.ride_id}`}
                          onClick={async () => {
                            if (!window.confirm(t('myRides.actions.confirmComplete'))) return;
                            try {
                              setActionLoading(true);
                              const token = localStorage.getItem('token');
                              await axios.post(`${API_URL}/wallet/complete-ride/${ride.ride_id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                              alert(t('myRides.actions.rideCompleted'));
                              fetchMyRidesAsDriver();
                            } catch (e) {
                              alert(e.response?.data?.detail || 'Error');
                            } finally {
                              setActionLoading(false);
                            }
                          }}
                          disabled={actionLoading}
                          className="flex items-center gap-1.5 bg-green-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-green-700 transition disabled:opacity-50"
                        >
                          <CheckCircle size={16} /> {t('myRides.actions.completeRide')}
                        </button>
                      </div>
                    )}
                    {ride.status === 'started' && (
                      <span className="flex items-center gap-1 text-green-600 font-bold text-sm bg-green-50 px-3 py-1.5 rounded-full border border-green-200">
                        <CheckCircle size={14} /> {t('myRides.status.completed')}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {ride.bookings && ride.bookings.length > 0 ? (
                      ride.bookings.map((b) => (
                        <div key={b.booking_id} className="bg-white p-4 rounded-lg border border-gray-200 flex justify-between items-center shadow-sm">
                          <div className="flex items-center gap-3">
                            <button 
                              onClick={() => handleViewPassengerProfile(b.passenger_id, b.passenger?.name)}
                              className="hover:opacity-80 transition flex items-center gap-3"
                            >
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="font-bold text-blue-600">
                                  {(b.passenger?.name || 'G').charAt(0)}
                                </span>
                              </div>
                              <div className="text-left">
                                <p className="font-bold text-gray-800 hover:text-blue-600 transition">
                                  {b.passenger?.name || t('common.passenger')}
                                </p>
                                <p className="text-sm text-gray-500">{b.seats_booked} {t('common.seats')}</p>
                                <div className="flex items-center gap-1 mt-1">
                                  <Star size={12} className="text-yellow-500 fill-yellow-500" />
                                  <span className="text-xs text-gray-600">
                                    {b.passenger?.rating || 'New'} ({b.passenger?.rating_count || 0})
                                  </span>
                                </div>
                              </div>
                            </button>
                          </div>
                          
                          <div className="flex flex-col items-end gap-2">
                            <div className="text-xs text-gray-500">
                              {new Date(b.created_at).toLocaleDateString(locale)}
                            </div>
                            <div className="flex items-center gap-2">
                              {b.status === 'pending' && (
                                <div className="flex gap-2">
                                  <button 
                                    onClick={() => handleConfirm(b.booking_id, b.passenger?.name)}
                                    className="bg-green-600 text-white px-4 py-1.5 rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50 transition"
                                    disabled={actionLoading}
                                  >
                                    {t('myRides.actions.confirm')}
                                  </button>
                                  <button 
                                    onClick={() => handleReject(b.booking_id, b.passenger?.name)}
                                    className="bg-gray-200 text-gray-700 px-4 py-1.5 rounded-lg text-sm font-bold hover:bg-gray-300 disabled:opacity-50 transition"
                                    disabled={actionLoading}
                                  >
                                    {t('myRides.actions.cancel')}
                                  </button>
                                </div>
                              )}
                              {b.status === 'confirmed' && (
                                <button 
                                  onClick={() => handleComplete(b.booking_id)} 
                                  className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition"
                                  disabled={actionLoading}
                                >
                                  {t('myRides.actions.complete')}
                                </button>
                              )}
                              {b.status === 'completed' && !b.rated_by_driver && (
                                <button
                                  data-testid={`rate-passenger-${b.booking_id}`}
                                  onClick={() => {
                                    setSelectedBooking({
                                      ...b,
                                      ride: { ...ride, driver_name: b.passenger?.name || t('common.passenger') },
                                      driver: { id: ride.driver_id },
                                    });
                                    setShowRatingModal(true);
                                  }}
                                  className="bg-yellow-500 text-white px-4 py-1.5 rounded-lg text-sm font-bold hover:bg-yellow-600 transition"
                                >
                                  ⭐ {t('myRides.actions.ratePassenger', 'Bewerten')}
                                </button>
                              )}
                              {b.status === 'completed' && b.rated_by_driver && (
                                <span className="text-green-600 font-bold text-sm bg-green-50 px-3 py-1 rounded-full border border-green-100">✓ {t('ratings.thanks')}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-400 italic">{t('myRides.bookings.noBookings')}</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'commutes' && (
        <div data-testid="commutes-tab-content" className="bg-white/95 backdrop-blur-md rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <p className="text-sm text-gray-700 max-w-2xl">
              {t('myRides.commute.subtitle',
                'Deine wiederkehrenden Pendler-Angebote (Hin & Rück). Pausiere für Urlaub/Homeoffice — keine Anrufe von Mitfahrern.')}
            </p>
            <a
              href="/create-commute"
              data-testid="commute-create-link"
              className="px-4 py-2 rounded-lg text-white font-semibold text-sm shadow-md transition whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #a855f7, #22c55e)' }}
            >
              + {t('myRides.commute.add', 'Neues Angebot')}
            </a>
          </div>

          {commuteOffersLoading ? (
            <div className="text-center text-gray-600 py-8">{t('common.loading', 'Lade…')}</div>
          ) : commuteOffers.length === 0 ? (
            <div data-testid="commutes-empty" style={{
              textAlign: 'center', padding: '40px 20px',
              background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(34,197,94,0.05))',
              border: '1px dashed rgba(168,85,247,0.4)',
              borderRadius: 16, color: '#475569',
            }}>
              <Repeat size={32} style={{ margin: '0 auto 12px', color: '#a855f7' }} />
              <p style={{ margin: 0, fontWeight: 500 }}>
                {t('myRides.commute.empty',
                  'Noch keine Pendler-Angebote. Lege deine erste Strecke an!')}
              </p>
            </div>
          ) : (
            <div data-testid="commute-offers-list">
              {commuteOffers.map(offer => (
                <CommuteOfferCard
                  key={offer.id}
                  offer={offer}
                  onPause={handlePauseOffer}
                  onResume={handleResumeOffer}
                  onDelete={handleDeleteOffer}
                  busy={busyOfferId === offer.id}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Passenger Profile Modal */}
      {showPassengerModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">{t('common.passenger')} {t('common.profile')}</h3>
              <button 
                onClick={() => setShowPassengerModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full transition"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              {passengerLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="ml-3 text-gray-600">{t('common.loading')}</p>
                </div>
              ) : (
                <>
                  {/* Profile Header */}
                  <div className="text-center mb-6">
                    <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-3xl font-bold">
                      {selectedPassenger?.name?.charAt(0) || '?'}
                    </div>
                    <h2 className="text-2xl font-bold mb-1">{selectedPassenger?.name || t('common.passenger')}</h2>
                    {selectedPassenger?.location && (
                      <p className="text-gray-500 flex items-center justify-center gap-1">
                        <MapPin size={16} />
                        {selectedPassenger.location}
                      </p>
                    )}
                  </div>

                  {/* Rating & Stats */}
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-xl mb-6">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Star size={20} className="text-yellow-500 fill-yellow-500" />
                      <span className="text-2xl font-bold">
                        {selectedPassenger?.average_rating ? Number(selectedPassenger.average_rating).toFixed(1) : 'New'}
                      </span>
                    </div>
                    <p className="text-center text-gray-600 text-sm mb-3">
                      {selectedPassenger?.total_ratings ? 
                        `${selectedPassenger.total_ratings} ${t('rideDetails.reviews')}` : 
                        t('profile.stats.reviews')}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div className="bg-white/50 p-2 rounded-lg">
                        <div className="font-bold text-blue-700">{selectedPassenger?.total_rides_as_passenger || 0}</div>
                        <div className="text-xs text-gray-600">{t('profile.stats.ridesAsPassenger')}</div>
                      </div>
                      <div className="bg-white/50 p-2 rounded-lg">
                        <div className="font-bold text-green-700">
                          {selectedPassenger?.verified_driver ? t('common.yes') : t('common.no')}
                        </div>
                        <div className="text-xs text-gray-600">{t('common.driver')}</div>
                      </div>
                    </div>
                  </div>

                  {/* Verification Status */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-700">{t('profile.verification.title')}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selectedPassenger?.verification_status === 'verified' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {selectedPassenger?.verification_status === 'verified' ? 
                          '✓ Verifiziert' : 'In Prüfung'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {selectedPassenger?.verified?.phone && (
                        <span className="flex items-center gap-1 text-green-600">
                          <Check size={14} />
                          Telefon
                        </span>
                      )}
                      {selectedPassenger?.verified?.email && (
                        <span className="flex items-center gap-1 text-green-600">
                          <Check size={14} />
                          E-Mail
                        </span>
                      )}
                      {selectedPassenger?.verified?.id && (
                        <span className="flex items-center gap-1 text-green-600">
                          <Check size={14} />
                          Ausweis
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Kontaktinfo (nur für Fahrer sichtbar) */}
                  <div className="mb-6">
                    <h4 className="font-bold mb-3 text-gray-700">Kontakt</h4>
                    <div className="space-y-2">
                      {selectedPassenger?.phone && (
                        <div className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition">
                          <Phone size={18} className="text-gray-400" />
                          <span className="font-medium">{selectedPassenger.phone}</span>
                        </div>
                      )}
                      {selectedPassenger?.email && (
                        <div className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition">
                          <Mail size={18} className="text-gray-400" />
                          <span className="font-medium truncate">{selectedPassenger.email}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Über mich */}
                  {selectedPassenger?.bio && (
                    <div className="mb-6">
                      <h4 className="font-bold mb-2 text-gray-700">Über mich</h4>
                      <p className="text-gray-600 bg-gray-50 p-4 rounded-lg border border-gray-100">
                        {selectedPassenger.bio}
                      </p>
                    </div>
                  )}

                  {/* Sprachen */}
                  {selectedPassenger?.languages && selectedPassenger.languages.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-bold mb-2 text-gray-700">Sprachen</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedPassenger.languages.map((lang, index) => (
                          <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Letzte Bewertungen */}
                  {passengerRatings.length > 0 && (
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-bold text-gray-700">Letzte Bewertungen</h4>
                        <span className="text-sm text-gray-500">{passengerRatings.length} Bewertungen</span>
                      </div>
                      <div className="space-y-3">
                        {passengerRatings.slice(0, 3).map((rating, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">
                                  {rating.rater_name?.charAt(0) || 'F'}
                                </div>
                                <div>
                                  <span className="font-medium text-sm">{rating.rater_name || 'Fahrer'}</span>
                                  {rating.ride_info && (
                                    <p className="text-xs text-gray-500">{rating.ride_info}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star 
                                    key={i} 
                                    size={14} 
                                    className={i < rating.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} 
                                  />
                                ))}
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">{rating.comment}</p>
                            {rating.created_at && (
                              <p className="text-xs text-gray-400 mt-2">
                                {new Date(rating.created_at).toLocaleDateString('de-DE')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Aktionen */}
                  <div className="space-y-3 pt-4 border-t">
                    <button 
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-bold hover:from-blue-700 hover:to-blue-800 transition flex items-center justify-center gap-2 shadow-lg"
                      onClick={() => {
                        // Hier könnte eine Chat-Funktion implementiert werden
                        alert('Chat-Funktion wird demnächst verfügbar sein!');
                      }}
                    >
                      <MessageSquare size={20} />
                      Nachricht senden
                    </button>
                    <button 
                      onClick={() => setShowPassengerModal(false)}
                      className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-bold hover:bg-gray-50 transition"
                    >
                      Schließen
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-2xl font-bold mb-2">Buchung stornieren</h3>
            <p className="text-gray-500 mb-6 text-sm">
              Möchtest du die Buchung für {selectedBooking.ride?.from_city} → {selectedBooking.ride?.to_city} wirklich stornieren?
            </p>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">Stornierungsgrund (optional):</label>
              <textarea 
                value={cancelReason} 
                onChange={(e) => setCancelReason(e.target.value)} 
                className="w-full border-2 border-gray-100 rounded-xl p-4 focus:border-blue-500 outline-none transition" 
                rows="3" 
                placeholder="Warum möchtest du stornieren?" 
              />
            </div>
            
            <div className="flex gap-4">
              <button 
                onClick={() => setShowCancelModal(false)} 
                className="flex-1 text-gray-500 font-bold py-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition"
              >
                Abbrechen
              </button>
              <button 
                onClick={handleCancel} 
                className="flex-1 bg-red-600 text-white py-3 rounded-xl font-bold hover:bg-red-700 shadow-lg shadow-red-200 transition disabled:opacity-50"
                disabled={actionLoading}
              >
                {actionLoading ? 'Wird storniert...' : 'Jetzt stornieren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedRideForModal && (
        <RideDetailsModal 
          ride={selectedRideForModal} 
          onClose={() => setSelectedRideForModal(null)} 
          onBook={null}
          mode={activeTab === 'driver' ? 'driver' : 'booking'}
          onCancelClick={() => {
             setSelectedRideForModal(null);
             setShowCancelModal(true);
          }}
        />
      )}

      {showRatingModal && selectedBooking && (
        <div data-testid="rating-modal" className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-2xl font-bold mb-2">{t('ratings.howWas', 'Wie war deine Fahrt?')}</h3>
            <p className="text-gray-500 mb-6 text-sm">
              {t('ratings.rateWith', 'Bewerte deine Fahrt mit {{name}}', { name: selectedBooking.ride?.driver_name || '' })}
            </p>

            <div className="flex justify-center space-x-3 mb-8">
              {[1, 2, 3, 4, 5].map(s => (
                <button
                  key={s}
                  data-testid={`rating-star-${s}`}
                  onClick={() => setRating(s)}
                  className={`text-4xl transition-transform hover:scale-110 ${s <= rating ? 'grayscale-0' : 'grayscale opacity-30'}`}
                >
                  ⭐
                </button>
              ))}
            </div>

            <textarea
              data-testid="rating-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full border-2 border-gray-100 rounded-xl p-4 mb-6 focus:border-blue-500 outline-none transition"
              rows="4"
              placeholder={t('ratings.feedbackPlaceholder', 'Schreibe ein kurzes Feedback…')}
            />

            <div className="flex gap-4">
              <button
                data-testid="rating-cancel"
                onClick={() => setShowRatingModal(false)}
                className="flex-1 text-gray-500 font-bold py-3 hover:text-gray-700 transition"
              >
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button
                data-testid="rating-submit"
                onClick={handleSubmitRating}
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition disabled:opacity-50"
                disabled={actionLoading}
              >
                {actionLoading ? t('common.loading', 'Wird gesendet…') : t('ratings.submit', 'Bewertung senden')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute Modal */}
      {showDisputeModal && disputeBooking && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={20} className="text-orange-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold">{t('disputes.openDispute')}</h3>
                <p className="text-sm text-gray-500">
                  {disputeBooking.ride?.from_city || disputeBooking.ride?.start_city} → {disputeBooking.ride?.to_city || disputeBooking.ride?.end_city}
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-4">
              {t('autoComplete.autoCompletedDesc')}
            </p>

            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-2">{t('disputes.disputeReason')}</label>
              <textarea
                data-testid="dispute-reason-input"
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl p-4 focus:border-orange-500 outline-none transition"
                rows="4"
                placeholder={t('disputes.disputeReasonPlaceholder')}
              />
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => { setShowDisputeModal(false); setDisputeReason(''); setDisputeBooking(null); }}
                className="flex-1 text-gray-500 font-bold py-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                data-testid="submit-dispute-btn"
                onClick={handleSubmitDispute}
                className="flex-1 bg-orange-500 text-white py-3 rounded-xl font-bold hover:bg-orange-600 shadow-lg shadow-orange-200 transition disabled:opacity-50"
                disabled={actionLoading || !disputeReason.trim()}
              >
                {actionLoading ? t('common.loading') : t('disputes.submitDispute')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Check Modal */}
      {showCameraCheck && cameraCheckRideId && (
        <CameraCheckModal
          rideId={cameraCheckRideId}
          onSuccess={async () => {
            setShowCameraCheck(false);
            try {
              setActionLoading(true);
              const token = localStorage.getItem('token');
              await axios.post(`${API_URL}/rides/${cameraCheckRideId}/start`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              fetchMyRidesAsDriver();
            } catch (e) {
              alert(e.response?.data?.detail || 'Error starting ride');
            } finally {
              setActionLoading(false);
              setCameraCheckRideId(null);
            }
          }}
          onClose={() => {
            setShowCameraCheck(false);
            setCameraCheckRideId(null);
          }}
        />
      )}
    </div>
  );
};

export default MyRides;